import {
  useCallback,
  useEffect,
  type FunctionComponent,
  type ReactNode,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { DynamicButton } from '@trello/dynamic-tokens';
import { Button } from '@trello/nachos/button';
import { CloseIcon } from '@trello/nachos/icons/close';
import type { GAModalTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import GAModalImagePngSrc from './beta-ga-modal-image.png';

import styles from './GAWelcomeModalWithTrial.less';

export const GAWelcomeModalWithTrial: FunctionComponent<{
  onClose: () => void;
  onContinue: () => void;
}> = ({ onClose, onContinue }) => {
  const intl = useIntl();

  const onClickCloseButton = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'closeGAModalButton',
      source: 'freeTrialGAModal',
    });
    onClose();
  }, [onClose]);

  const onClickDismiss = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'dismissFreeTrialGAModalButton',
      source: 'freeTrialGAModal',
    });
    onClose();
  }, [onClose]);

  const onClickContinue = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'freeTrialGAModalButton',
      source: 'freeTrialGAModal',
    });
    onContinue();
  }, [onContinue]);

  useEffect(() => {
    Analytics.sendViewedComponentEvent({
      componentType: 'modal',
      componentName: 'freeTrialGAModal',
      source: 'boardScreen',
    });
  }, []);

  return (
    <div className={styles.gaWelcomeModal}>
      <img className={styles.headingImage} src={GAModalImagePngSrc} alt="" />
      <DynamicButton
        className={styles.closeButton}
        iconBefore={<CloseIcon size="medium" />}
        onClick={onClickCloseButton}
        aria-label={intl.formatMessage({
          id: 'templates.dialog_close_button.close-dialog-label',
          defaultMessage: 'Close dialog',
          description: 'Aria label for the close dialog button',
        })}
        data-testid={getTestId<GAModalTestIds>('ga-modal-close-button')}
      />
      <div className={styles.contentContainer}>
        <FormattedMessage
          id="templates.beta_ga.the-new-trello"
          defaultMessage="The new Trello is officially here"
          description="GA modal content"
          tagName="h1"
        />
        <p>
          <FormattedMessage
            id="templates.beta_ga.thanks-for-trying-trello-beta"
            defaultMessage="Thanks for trying Trello Beta and sharing your feedback!"
            description="GA modal content"
            tagName="span"
          />
          &nbsp;
          <FormattedMessage
            id="templates.beta_ga.scheduling-in-planner"
            defaultMessage="Scheduling in Planner and AI for Quick Capture are now part of our <strong>Standard</strong> and <strong>Premium</strong> plans."
            description="GA modal content"
            tagName="strong"
            values={{
              strong: (chunks: ReactNode) => <strong>{chunks}</strong>,
            }}
          />
        </p>
        <FormattedMessage
          id="templates.beta_ga.try-premium-to-unlock"
          defaultMessage="Try Premium to unlock full productivity power."
          description="GA modal content"
          tagName="p"
        />
        <div className={styles.ctasContainer}>
          <Button appearance="subtle" onClick={onClickDismiss}>
            <FormattedMessage
              id="templates.beta_ga.ga-live-modal-dismiss-button"
              defaultMessage="Dismiss"
              description="GA modal dismiss button"
            />
          </Button>
          <Button appearance="primary" onClick={onClickContinue}>
            <FormattedMessage
              id="templates.beta_ga.try-premium-free"
              defaultMessage="Try Premium free"
              description="GA modal free trial button"
            />
          </Button>
        </div>
      </div>
    </div>
  );
};
